import * as THREE from 'three';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { TextureLoader } from 'three/src/loaders/TextureLoader';
import vertexYolkShader from './../shaders/vertex.glsl?raw';
import fragmentYolkShader from './../shaders/fragment.glsl?raw';

let fractAmountYolk = 70;
let counterSine = 0;
let incrementCounter = true;
const rotationDefaultSpeedEgg = 0.005;
let coeffOfSpeedEgg = 1;
const Component = ({
  setDisplayedSpeed,
  setDisplayedMaxpeed,
  displaySpeed,
}: {
  setDisplayedSpeed: (value: number | ((prev: number) => number)) => void;
  setDisplayedMaxpeed: (value: number | ((prev: number) => number)) => void;
  displaySpeed: number;
}) => {
  const eggRef = useRef<THREE.Mesh>(null);
  const clickTimeoutRef = useRef<number | null>(null);
  const [isClicking, setClicking] = useState(false);
  const [timestamps, setTimestamps] = useState<{
    prevClick: null | number;
    lastClick: null | number;
    timeDifference: null | number;
  }>({ prevClick: null, lastClick: null, timeDifference: null });

  const widhtEgg = 0.72;
  const shapeEgg = 0.03;
  const sizeEgg = 1;
  const sizeYolk = 0.3;
  const widhtYolk = 0.62;
  const shapeYolk = 0.08;
  const speedYolk = 1000;
  const timeClikedDelay = 250;

  const latestSpeedRef = useRef(0);
  const smoothedCoeffOfSpeedEgg = useRef(1);

  function getEaseInOutQuad(t: number) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  const handleTouchStart = () => {
    const now = Date.now();
    if (!isClicking) {
      setClicking(true);
    }
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = setTimeout(() => {
      setClicking(false);
      clickTimeoutRef.current = null;
    }, timeClikedDelay);

    setTimestamps((prevState) => {
      const timeDifference = prevState.lastClick
        ? now - prevState.lastClick
        : 0;
      return {
        prevClick: prevState.lastClick,
        lastClick: now,
        timeDifference: timeDifference,
      };
    });
  };
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('click', handleTouchStart);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('click', handleTouchStart);
    };
  }, []);

  useEffect(() => {
    const smoothingFactor = 0.05;
    const updateMaxSpeedAfterDelay = () => {
      const maxSpeed = displaySpeed;
      setDisplayedMaxpeed((prevMaxSpeed) => {
        return maxSpeed > prevMaxSpeed ? maxSpeed : prevMaxSpeed;
      });
    };

    const interval = setInterval(() => {
      setDisplayedSpeed((prev: number): number => {
        const diff: number = latestSpeedRef.current - prev;
        return prev + diff * smoothingFactor;
      });
    }, 120);

    updateMaxSpeedAfterDelay();

    return () => clearInterval(interval);
  }, [displaySpeed]);

  useFrame((_, delta) => {
    const timeDependence = delta * 50;

    // Counter for constant sine animation, 0 - stop, 100 - max
    if (counterSine > 100) incrementCounter = false;
    if (counterSine < 0) incrementCounter = true;
    if (counterSine > -3 && counterSine < 100 && incrementCounter) {
      counterSine = counterSine + timeDependence;
    } else if (counterSine <= 103 && !incrementCounter) {
      counterSine = counterSine - timeDependence;
    }

    // Fraction amount speed, 8 - fast, 70 - slow
    if (
      isClicking &&
      fractAmountYolk > 8 &&
      timestamps.timeDifference &&
      timestamps.timeDifference < 100
    )
      fractAmountYolk = fractAmountYolk - timeDependence;
    if (
      isClicking &&
      fractAmountYolk > 10 &&
      timestamps.timeDifference &&
      timestamps.timeDifference < 130
    )
      fractAmountYolk = fractAmountYolk - timeDependence;
    if (
      isClicking &&
      fractAmountYolk > 14 &&
      timestamps.timeDifference &&
      timestamps.timeDifference < 200
    )
      fractAmountYolk = fractAmountYolk - timeDependence;
    if (isClicking && fractAmountYolk > 30)
      fractAmountYolk = fractAmountYolk - timeDependence;
    if (!isClicking && fractAmountYolk < 70)
      fractAmountYolk = fractAmountYolk + timeDependence;

    // Speed coefficient
    if (!isClicking && coeffOfSpeedEgg > 1) coeffOfSpeedEgg -= delta;
    if (isClicking && timestamps.timeDifference) {
      const speedFactor = 1 - Math.min(1, timestamps.timeDifference / 1000);
      coeffOfSpeedEgg = 1 + speedFactor * 3;
    }

    // Smooth animation coefficient
    const smoothingFactor = 0.05;
    smoothedCoeffOfSpeedEgg.current +=
      (coeffOfSpeedEgg - smoothedCoeffOfSpeedEgg.current) * smoothingFactor;

    // Rotation Egg
    if (eggRef.current) {
      eggRef.current.rotation.y +=
        rotationDefaultSpeedEgg *
        timeDependence *
        smoothedCoeffOfSpeedEgg.current;
      latestSpeedRef.current = rotationDefaultSpeedEgg * coeffOfSpeedEgg * 1000;
    }
  });

  const getEggShapeGeometry = (
    widht: number,
    shape: number,
    size: number,
    segments: number,
  ) => {
    const points: THREE.Vector2[] = [];
    for (let deg = 0; deg <= 180; deg += 1) {
      const rad = (Math.PI * deg) / 180;
      const point = new THREE.Vector2(
        (widht + shape * Math.cos(rad)) * Math.sin(rad) * size,
        -Math.cos(rad) * size,
      );
      points.push(point);
    }

    const geometry = useMemo(() => {
      const geometry = mergeVertices(new THREE.LatheGeometry(points, segments));
      geometry.computeTangents();
      return geometry;
    }, []);

    return geometry;
  };

  // TODO move in one function
  // EggShell Texture
  const getNameEggShellTexture = (type: string) => `./texture/egg/${type}.png`;
  const [eggShellRoughnessMap] = useLoader(TextureLoader, [
    getNameEggShellTexture('roughness'),
  ]);
  const repeatEggTextures = (texture: {
    wrapS: number;
    wrapT: number;
    repeat: { x: number; y: number };
  }) => {
    const repeat = 0.1;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.x = repeat * 20;
    texture.repeat.y = repeat * 20;
  };
  repeatEggTextures(eggShellRoughnessMap);
  //

  // EggNoise Texture
  const getNameEggNoiseTexture = (type: string) =>
    `./texture/noise/${type}.png`;
  const [eggNoiseMetalnessMap] = useLoader(TextureLoader, [
    getNameEggNoiseTexture('noise'),
  ]);
  const repeatEggNoiseTextures = (texture: {
    wrapS: number;
    wrapT: number;
    repeat: { x: number; y: number };
  }) => {
    const repeat = 0.1;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.x = repeat * 20;
    texture.repeat.y = repeat * 20;
  };
  repeatEggNoiseTextures(eggNoiseMetalnessMap);

  const YolkShaderMaterial = React.forwardRef((props, ref) => {
    const yolkUniforms = {
      uColor: { value: new THREE.Color('#fdeb75') },
      uSpeed: { value: 0 },
      uNoiseStrength: { value: 0.5 },
      uDisplacementStrength: { value: 0.5 },
      uFractAmount: { value: 0 },
      lightDirection: { value: new THREE.Vector3(5, 2, 1).normalize() },
      ambientColor: { value: new THREE.Vector3(1, 1, 1).normalize() },
      lightIntensity: { value: 0.5 },
    };

    const shaderMaterial = useMemo(
      () =>
        new THREE.ShaderMaterial({
          vertexShader: vertexYolkShader,
          fragmentShader: fragmentYolkShader,
          uniforms: yolkUniforms,
        }),
      [],
    );

    useFrame(() => {
      // Yolk animation
      if (shaderMaterial.uniforms) {
        shaderMaterial.uniforms.uSpeed.value =
          (getEaseInOutQuad(counterSine / 100) * 100) / speedYolk;
        shaderMaterial.uniforms.uFractAmount.value =
          (getEaseInOutQuad(counterSine / 100) * 100) / fractAmountYolk;
      }
    });

    return (
      <primitive
        object={shaderMaterial}
        ref={ref}
        attach="material"
        {...props}
      />
    );
  });

  return (
    <>
      <mesh ref={eggRef}>
        <mesh
          position={[0, 0, 0]}
          rotation={[0, 10, 0]}
          geometry={getEggShapeGeometry(widhtYolk, shapeYolk, sizeYolk, 10)}
        >
          <YolkShaderMaterial />
        </mesh>
        {/* START BLUR */}
        <mesh
          rotation={[0, 10, 0]}
          geometry={getEggShapeGeometry(widhtEgg, shapeEgg, sizeEgg, 10)}
        >
          <meshPhysicalMaterial
            color="#fdeb75"
            metalness={0.2}
            roughness={0.3}
            transparent={true}
            opacity={0.7}
            transmission={1}
          />
        </mesh>
        {/* END BLUR */}
        <mesh
          rotation={[0, 10, 0]}
          geometry={getEggShapeGeometry(widhtEgg, shapeEgg, sizeEgg, 30)}
        >
          <meshPhysicalMaterial
            displacementScale={0}
            displacementBias={0}
            metalnessMap={eggNoiseMetalnessMap}
            roughnessMap={eggShellRoughnessMap}
            color="#fdeb75"
            metalness={0.3}
            roughness={0.9}
            transmission={1}
            thickness={0.95}
            transparent={true}
            opacity={0.6}
          />
        </mesh>
      </mesh>
      {/* TODO ambient don't used? */}
      <ambientLight color="#fff" intensity={0.2} />
      <directionalLight color="#fff" intensity={4} position={[20, 10, 2]} />
    </>
  );
};

export default Component;
